import React, { useEffect, useMemo } from 'react'
import { RouteComponentProps, useHistory } from 'react-router'
import { connect } from 'react-redux'
import { deleteSearchParams, setSearchParamValue } from 'lib/url/searchUrlUtils'
import { fetchPlaceByName } from 'actions/PlaceActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Heading from 'LuxKit/Typography/Heading'
import { getImageUrl } from 'lib/image/imageUtils'
import Places from 'constants/places'
import ExperienceSearchPage from '../ExperiencesSearch/ExperienceSearchPage'
import {
  normaliseLocationSlugForLookup,
  plusSlugFromText,
  resolveCategoryCodesFromSlug,
  slugFromText,
} from '../../../lib/experiencesListUtils'
import { getDestinationImage } from 'lib/search/searchUtils'
import { toTitleCase } from 'lib/string/stringUtils'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'

type RouteParams = {
  regionCode: string
  locationSlug: string
  categorySlug?: string
}

interface MappedStateProps {
  windowSearch: string
  locationToPlace: App.State['destination']['locationToPlace']
  placesLoading: App.State['destination']['placesLoading']
  categories: App.State['experience']['experienceCategories']
}

type Props = RouteComponentProps<RouteParams> & MappedStateProps

const Banner = styled.div<{ imageUrl?: string }>`
  width: 100vw;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: ${rem(48)};

  & > h1, & > h2 {
    margin: 0 auto;
    padding: 0;
  }

  ${mediaQueryUp.tablet} {
    margin-bottom: ${rem(16)};
  }

  background: linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), url(${props => props.imageUrl}) lightgray 50% / cover no-repeat;
`

function getCategoryBySlug(categorySlug: string | undefined, categories: Array<App.ExperienceItemCategory>): App.ExperienceItemCategory | undefined {
  if (!categorySlug) return undefined

  const slug = categorySlug.trim().toLowerCase()
  return categories.find((category) => (
    plusSlugFromText(category.name) === slug ||
    slugFromText(category.name) === slug
  ))
}

function isValidCategoryImageId(imageId?: string) {
  const INVALID_CATEGORY_IMAGE_ID = '4nz573bb82uubd28k0k'
  return !!imageId && imageId !== INVALID_CATEGORY_IMAGE_ID
}

// Picks a category image ID using the path slug match (with parent fallback).
function getCategoryImageId(
  categories: Array<App.ExperienceItemCategory>,
  matchedCategory?: App.ExperienceItemCategory,
): string | undefined {
  const matchedImageId = matchedCategory?.imageId
  if (isValidCategoryImageId(matchedImageId)) return matchedImageId

  const parentCategory = matchedCategory?.parentId
    ? categories.find((category) => category.id === matchedCategory.parentId)
    : undefined

  return isValidCategoryImageId(parentCategory?.imageId) ? parentCategory?.imageId : undefined
}

function ExperiencesListPage(props: Props) {
  const { locationSlug, categorySlug } = props.match.params
  const {
    windowSearch: search,
    categories,
    locationToPlace,
    placesLoading,
  } = props
  const history = useHistory()
  const dispatch = useAppDispatch()
  const normalizedSearch = search.startsWith('?') ? search.slice(1) : search
  const searchParams = useMemo(() => new URLSearchParams(normalizedSearch), [normalizedSearch])

  const locationKey = useMemo(() => {
    return normaliseLocationSlugForLookup(locationSlug)
  }, [locationSlug])

  const isAnywhereSlug = locationKey === Places.Anywhere.name.toLowerCase()
  const destinationPlace = isAnywhereSlug ? Places.Anywhere : locationToPlace[locationKey]
  const isFetchingPlace = placesLoading[locationKey]

  useEffect(() => {
    if (!isAnywhereSlug && !destinationPlace && !isFetchingPlace) {
      dispatch(fetchPlaceByName(locationKey))
    }
  }, [dispatch, destinationPlace, isFetchingPlace, isAnywhereSlug, locationKey])

  const resolvedCategoryCodes = useMemo(() => {
    if (!categorySlug) return undefined
    if (!categories?.length) return undefined

    return resolveCategoryCodesFromSlug({
      categorySlug,
      categories,
    })
  }, [categorySlug, categories])

  const matchedCategory = useMemo(() => {
    if (!categorySlug || !categories?.length) return undefined
    return getCategoryBySlug(categorySlug, categories)
  }, [categorySlug, categories])

  const heroTitle = useMemo(() => {
    const placeName = destinationPlace?.name ?? ''
    const locationTitle = placeName ? toTitleCase(placeName) : ''
    if (!locationTitle) return 'Experiences'
    if (matchedCategory) {
      return `${locationTitle} ${toTitleCase(matchedCategory.name)}`
    }
    return `${locationTitle} Experiences`
  }, [destinationPlace, matchedCategory])

  const heroImageUrl = useMemo(() => {
    const categoryImageId = getCategoryImageId(categories, matchedCategory)
    const destinationImageId = destinationPlace && 'id' in destinationPlace ? getDestinationImage(destinationPlace.id) : undefined
    const imageId = categoryImageId || destinationImageId || 'nz83bzbyhxc8r1b8t8m'
    return getImageUrl(imageId)
  }, [categories, destinationPlace, matchedCategory])

  const nextSearch = useMemo(() => {
    const hasDestinationParams = searchParams.has('destinationId')
    const hasExperienceParams = searchParams.has('experienceId')
    const hasCategoryParams = searchParams.has('categories')

    if (!destinationPlace || !('id' in destinationPlace) || !destinationPlace.id) {
      if (hasDestinationParams) {
        return normalizedSearch
      }
      return deleteSearchParams(normalizedSearch, 'destinationId', 'destinationName', 'categories', 'experienceId', 'experienceName')
    }

    let updatedSearch = normalizedSearch

    if (!hasDestinationParams) {
      updatedSearch = setSearchParamValue(updatedSearch, 'destinationId', destinationPlace.id)
      updatedSearch = setSearchParamValue(updatedSearch, 'destinationName', destinationPlace.name)
    }

    if (!hasExperienceParams) {
      updatedSearch = deleteSearchParams(updatedSearch, 'experienceId', 'experienceName')
    }

    if (!hasCategoryParams) {
      if (resolvedCategoryCodes?.length) {
        updatedSearch = setSearchParamValue(updatedSearch, 'categories', resolvedCategoryCodes.join(','))
        return updatedSearch
      }

      return deleteSearchParams(updatedSearch, 'categories')
    }

    return updatedSearch
  }, [destinationPlace, normalizedSearch, resolvedCategoryCodes, searchParams])

  useEffect(() => {
    if (nextSearch !== normalizedSearch) {
      history.replace({
        search: nextSearch ? `?${nextSearch}` : '',
      })
    }
  }, [history, nextSearch, normalizedSearch])
  if (nextSearch !== normalizedSearch) {
    return <LoadingIndicator />
  }
  if (!destinationPlace && isFetchingPlace) {
    return <LoadingIndicator />
  }
  return (
    <>
      <Banner imageUrl={heroImageUrl}>
        <Heading variant="heading2" align="center" as="h1" colour="neutral-eight">{heroTitle}</Heading>
      </Banner>
      <ExperienceSearchPage forceCompactTiles />
    </>
  )
}

function mapStateToProps(state: App.State): MappedStateProps {
  return {
    windowSearch: state.router.location.search,
    locationToPlace: state.destination.locationToPlace,
    placesLoading: state.destination.placesLoading,
    categories: state.experience.experienceCategories,
  }
}

export default connect(mapStateToProps)(ExperiencesListPage)
